import { ShieldCheck } from "lucide-react";
import { formatDate, formatNumber } from "../lib/format";
import type { DashboardManifest, DatasetHealth } from "../lib/types";
import { Button } from "./ui/button";
import { StatusBadge, type StatusBadgeVariant } from "./ui/status-badge";

type TrustStatusBarProps =
  | { availability: "loading" }
  | { availability: "error" }
  | {
      availability: "ready";
      manifest: DashboardManifest;
      health: DatasetHealth;
      loadedRowCount: number;
      onOpenCoverage: () => void;
    };

const modeLabel: Record<DatasetHealth["datasetMode"], string> = {
  live: "Live dataset",
  demo: "Demo dataset",
  partial: "Partial dataset"
};

const validityLabel: Record<DatasetHealth["validityState"], string> = {
  valid: "Valid dataset",
  degraded: "Degraded dataset",
  invalid: "Invalid dataset"
};

const ingestLabel: Record<DatasetHealth["ingestState"], string> = {
  completed: "Completed ingest",
  incomplete: "Incomplete ingest",
  failed: "Failed ingest"
};

function signalVariant(value: "good" | "warning" | "error" | "neutral"): StatusBadgeVariant {
  if (value === "good") return "buy";
  if (value === "warning") return "warning";
  if (value === "error") return "destructive";
  return "neutral";
}

const utcTimestampFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  timeZone: "UTC",
  timeZoneName: "short"
});

function formatUtcTimestamp(value: string): string {
  return utcTimestampFormatter.format(new Date(value));
}

/** Weekday SEC refresh targets in America/Los_Angeles: pre-open ~6:10, midday ~12:10, post-close ~16:40. */
export function nextRefreshWindowLabel(now = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).formatToParts(now);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
  const weekday = get("weekday");
  const hour = Number(get("hour"));
  const minute = Number(get("minute"));
  const mins = hour * 60 + minute;
  const isWeekend = weekday === "Sat" || weekday === "Sun";
  const windows = [
    { label: "pre-open (~6:10 PT)", at: 6 * 60 + 10 },
    { label: "midday (~12:10 PT)", at: 12 * 60 + 10 },
    { label: "post-close (~16:40 PT)", at: 16 * 60 + 40 }
  ];
  if (isWeekend) return "next weekday pre-open (~6:10 PT)";
  for (const window of windows) {
    if (mins < window.at) return window.label;
  }
  return "next weekday pre-open (~6:10 PT)";
}


export function TrustStatusBar(props: TrustStatusBarProps) {
  if (props.availability === "loading") {
    return (
      <section
        aria-label="Dataset trust status"
        className="flex h-[34px] items-center border-b border-border bg-surface-inset px-3"
      >
        <StatusBadge variant="neutral">Validating dataset</StatusBadge>
      </section>
    );
  }

  if (props.availability === "error") {
    return (
      <section
        aria-label="Dataset trust status"
        className="flex h-[34px] items-center border-b border-destructive bg-surface-inset px-3"
      >
        <StatusBadge variant="destructive">Dataset unavailable</StatusBadge>
      </section>
    );
  }

  const { manifest, health, loadedRowCount, onOpenCoverage } = props;
  const rowLabel = `${formatNumber(loadedRowCount)} validated ${loadedRowCount === 1 ? "row" : "rows"}`;

  return (
    <section
      aria-label="Dataset trust status"
      className={`flex h-[34px] items-center gap-2 overflow-hidden border-b px-3 text-[11px] ${
        health.isLiveTrusted
          ? "border-border bg-surface-inset"
          : health.freshness === "stale" || health.validityState === "invalid"
            ? "border-destructive bg-destructive/10"
            : "border-warning bg-warning-soft"
      }`}
    >
      <div className="flex min-w-0 shrink items-center gap-1">
        <StatusBadge
          variant={
            health.isLiveTrusted
              ? "buy"
              : health.datasetMode === "live" && health.freshness === "fresh"
                ? "warning"
                : "destructive"
          }
        >
          {health.isLiveTrusted
            ? "Trusted live"
            : health.datasetMode === "live"
              ? health.freshness === "fresh"
                ? "Live not trusted"
                : "Live stale"
              : "Not live"}
        </StatusBadge>
        <StatusBadge variant={health.datasetMode === "live" ? "neutral" : "warning"}>
          {modeLabel[health.datasetMode]}
        </StatusBadge>
        <StatusBadge variant={signalVariant(health.freshness === "fresh" ? "good" : "warning")}>
          {health.freshness === "fresh" ? "Fresh data" : "Stale data"}
        </StatusBadge>
        <StatusBadge
          className="hidden sm:inline-flex"
          variant={signalVariant(
            health.validityState === "valid"
              ? "good"
              : health.validityState === "degraded"
                ? "warning"
                : "error"
          )}
        >
          {validityLabel[health.validityState]}
        </StatusBadge>
        <StatusBadge
          className="hidden lg:inline-flex"
          variant={signalVariant(
            health.ingestState === "completed"
              ? "good"
              : health.snapshotFromLastCompletedRun
                ? "warning"
                : "error"
          )}
        >
          {ingestLabel[health.ingestState]}
        </StatusBadge>
        {health.snapshotFromLastCompletedRun ? (
          <StatusBadge variant="warning">Last completed snapshot</StatusBadge>
        ) : null}
      </div>

      <div className="ml-auto flex min-w-0 shrink items-center gap-3 text-muted-foreground">
        <span className="hidden 2xl:inline">Generated: {formatUtcTimestamp(manifest.generatedAt)}</span>
        <span className="hidden xl:inline">
          Age:{" "}
          {health.generatedAgeHours === null
            ? "n/a"
            : `${health.generatedAgeHours < 10 ? health.generatedAgeHours.toFixed(1) : Math.round(health.generatedAgeHours)}h`}
        </span>
        <span className="hidden 2xl:inline">
          SLA:{" "}
          {health.freshness === "fresh"
            ? `within window · next ${nextRefreshWindowLabel()}`
            : health.generatedAgeHours !== null && health.generatedAgeHours > 36
              ? `behind target · next ${nextRefreshWindowLabel()}`
              : `monitor cadence · next ${nextRefreshWindowLabel()}`}
        </span>
        <span className="hidden xl:inline">Latest filing: {formatDate(manifest.latestFilingProcessed)}</span>
        <span className="hidden md:inline">{rowLabel}</span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-6 px-2"
          onClick={onOpenCoverage}
        >
          <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
          Coverage
        </Button>
      </div>
    </section>
  );
}
